import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface LineItemData {
  name: string;
  start_date: string;
  end_date: string;
  type: string;
  rate: number | null;
  booked: number | null;
  impression: number | null;
  clicks: number | null;
  ctr: string;
  conversion: number | null;
  completed_views: number | null;
  estimated_spend: number | null;
  completion_rate: string;
  pacing: number | null;
  monthly_rate: number | null;
  monthly_hours: number | null;
  monthly_amount: number | null;
  retainer_amount: number | null;
}

export interface ReportsState {
  status: string;
  lineItems: LineItemData[];
  selectedReportOrder: {
    id: string;
    name: string;
  } | null;
  startDate: string;
  endDate: string;
  reportDataBy: 'day' | 'week' | 'month' | 'quarter' | 'year';
  selectedExchangesData: any[];
  visibleExchangeIds: number[];
  exchangeOrder: number[];
}

//all selected exchanges state interface for reference
// This interface represents the structure of each exchange in the allSelectedExchanges state
export interface selectedExchangesData {
  id: number;
  name: string;
  iconUrl: string;
  selectedMetrics: string[];
  defaultMetrics: string[];
  selectedLineItem: string[];
  display_metrics: string[];
  dataBy: 'day' | 'week' | 'month' | 'quarter' | 'year';
  hasData?: boolean;
  emptyColumns?: string[];
  exchanges: {
    exchange_id: number;
    exchange_console_name: string;
    exchange_table: string;
    metrics: string[];
  }[];
  lineItems: {
    Id: string;
    Line_Item_Name__c: string;
    exchange: number;
    Media_Type__c: string;
  }[];
}

const initialState: ReportsState = {
  status: '',
  lineItems: [],
  selectedReportOrder: null,
  startDate: "",
  endDate: "",
  reportDataBy: 'week',
  selectedExchangesData: [],
  visibleExchangeIds: [],
  exchangeOrder: [],
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setStatus(state, action: PayloadAction<string>) {
      state.status = action.payload;
    },
    setLineItemData(state, action: PayloadAction<LineItemData[]>) {
      state.lineItems = action.payload;
    },
    setSelectedReportOrder(state, action: PayloadAction<{ id: string; name: string }>) {
      state.selectedReportOrder = {
        id: action.payload.id,
        name: action.payload.name,
      };
    },
    setStartDate(state, action: PayloadAction<string>) {
      state.startDate = action.payload;
    },
    setEndDate(state, action: PayloadAction<string>) {
      state.endDate = action.payload;
    },
    setDateRange(
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>
    ) {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },

    setReportDataBy(state, action: PayloadAction<'day' | 'week' | 'month' | 'quarter' | 'year'>) {
      const newReportDataBy = action.payload;

      // Update the main reportDataBy
      state.reportDataBy = newReportDataBy;

      // Update reportDataBy for all selected exchanges
      if (state.selectedExchangesData && state.selectedExchangesData.length > 0) {
        state.selectedExchangesData.forEach(exchange => {
          exchange.dataBy = newReportDataBy;
        });
      }
    },

    setSelectedExchangesData(state, action: PayloadAction<any[]>) {
      state.selectedExchangesData = action.payload;
    },


    setSelectedExchangeMetrics: (
      state,
      action: PayloadAction<{
        exchangeName: string;
        metrics: string[];
      }>
    ) => {
      const { exchangeName, metrics } = action.payload;

      // Input validation
      if (!exchangeName || !Array.isArray(metrics)) {
        console.warn('Invalid payload for setSelectedExchangeMetrics');
        return;
      }

      const normalizedSearch = exchangeName.trim().toLowerCase();

      // Find the exchange and safely update metrics
      const exchangeIndex = state.selectedExchangesData.findIndex(
        (ex) => ex.name.trim().toLowerCase() === normalizedSearch
      );

      if (exchangeIndex >= 0) {
        // Using Immer (built into Redux Toolkit), we can safely "mutate" the state
        state.selectedExchangesData[exchangeIndex].selectedMetrics = metrics;
      } else {
        console.warn(`Exchange "${exchangeName}" not found in selected exchanges`);
      }
    },

    setSelectedExchangeLineItems(
      state,
      action: PayloadAction<{
        exchangeName: string;
        lineItems: string[];
      }>
    ) {
      const { exchangeName, lineItems } = action.payload;
      const normalizedSearch = exchangeName.trim().toLowerCase();

      const exchange = state.selectedExchangesData.find(
        (ex) => ex.name.trim().toLowerCase() === normalizedSearch
      );

      if (exchange) {
        exchange.selectedLineItems = lineItems;
      }
    },

    // Get exchange by name action
    getExchangeByName: {
      reducer: () => { }, // No state mutation
      prepare: (exchangeName: string) => ({
        payload: { exchangeName: exchangeName.trim().toLowerCase() }
      })
    },

    setExchangeDataBy(
      state,
      action: PayloadAction<{
        exchangeName: string;
        dataBy: 'day' | 'week' | 'month' | 'quarter' | 'year';
      }>
    ) {
      const { exchangeName, dataBy } = action.payload;

      // Input validation
      if (!exchangeName || !dataBy) {
        console.warn('Invalid payload for setExchangeDataBy');
        return;
      }

      const normalizedSearch = exchangeName.trim().toLowerCase();

      // Find the exchange and update reportDataBy
      const exchangeIndex = state.selectedExchangesData.findIndex(
        (ex) => ex.name.trim().toLowerCase() === normalizedSearch
      );

      if (exchangeIndex >= 0) {
        state.selectedExchangesData[exchangeIndex].dataBy = dataBy;
      } else {
        console.warn(`Exchange "${exchangeName}" not found in selected exchanges`);
      }
    },

    setExchangeHasData(
      state,
      action: PayloadAction<{
        exchangeName: string;
        hasData: boolean;
      }>
    ) {
      const { exchangeName, hasData } = action.payload;

      // Input validation
      if (!exchangeName || hasData === undefined) {
        console.warn('Invalid payload for setExchangeHasData');
        return;
      }

      const normalizedSearch = exchangeName.trim().toLowerCase();

      // Find the exchange and update hasData status
      const exchangeIndex = state.selectedExchangesData.findIndex(
        (ex) => ex.name.trim().toLowerCase() === normalizedSearch
      );

      if (exchangeIndex >= 0) {
        state.selectedExchangesData[exchangeIndex].hasData = hasData;
      } else {
        console.warn(`Exchange "${exchangeName}" not found in selected exchanges`);
      }
    },

    // Update empty columns for a specific exchange
    // This stores which columns have all empty values to hide them in the UI
    setVisibleExchangeIds(state, action: PayloadAction<number[]>) {
      state.visibleExchangeIds = action.payload;
    },
    resetVisibleExchangeIds(state) {
      state.visibleExchangeIds = [];
    },
    setExchangeOrder(state, action: PayloadAction<number[]>) {
      state.exchangeOrder = action.payload;
    },
    resetExchangeOrder(state) {
      state.exchangeOrder = [];
    },

    setExchangeEmptyColumns(
      state,
      action: PayloadAction<{
        exchangeName: string;
        emptyColumns: string[];
      }>
    ) {
      const { exchangeName, emptyColumns } = action.payload;

      // Input validation
      if (!exchangeName || !Array.isArray(emptyColumns)) {
        console.warn('Invalid payload for setExchangeEmptyColumns');
        return;
      }

      const normalizedSearch = exchangeName.trim().toLowerCase();

      // Find the exchange and update empty columns
      const exchangeIndex = state.selectedExchangesData.findIndex(
        (ex) => ex.name.trim().toLowerCase() === normalizedSearch
      );

      if (exchangeIndex >= 0) {
        state.selectedExchangesData[exchangeIndex].emptyColumns = emptyColumns;
      } else {
        console.warn(`Exchange "${exchangeName}" not found in selected exchanges`);
      }
    }

  }
});

// --- Selectors ---
export const selectExchangeByName = (state: ReportsState, exchangeName: string) => {
  const normalizedSearch = exchangeName.trim().toLowerCase();
  return state.selectedExchangesData.find(
    ex => ex.name.trim().toLowerCase() === normalizedSearch
  );
};

// Check if all exchanges have no data
export const selectAllExchangesHaveNoData = (state: ReportsState) => {
  if (!state.selectedExchangesData || state.selectedExchangesData.length === 0) {
    return true; // No exchanges selected means no data
  }

  return state.selectedExchangesData.every(exchange => exchange.hasData === false);
};

export const { setLineItemData,
  setSelectedReportOrder,
  setStartDate,
  setEndDate,
  setDateRange,
  setReportDataBy,
  setSelectedExchangesData,
  setSelectedExchangeMetrics,
  setSelectedExchangeLineItems,
  getExchangeByName,
  setExchangeDataBy,
  setExchangeHasData,
  setExchangeEmptyColumns,
  setVisibleExchangeIds,
  resetVisibleExchangeIds,
  setExchangeOrder,
  resetExchangeOrder
} = reportsSlice.actions;
export default reportsSlice.reducer;
